import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import * as pickerService from '../services/api/picker';
import * as ordersService from '../services/api/orders';
import { transformOrder } from '../services/transformers/orderTransformer';
import { 
  ArrowLeftIcon,
  ArrowRightIcon, 
  PackageIcon, 
  PlusIcon, 
  MinusIcon,
  CheckIcon,
  ScaleIcon,
  EditIcon
} from '../components/Icons';
import './PickupProcess.css';

const PickupProcess = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { currentPickup, pickupItems, setPickupItems, updatePickupItem, plasticRates, setCurrentPickup } = useApp();
  const { theme } = useTheme();
  const [showMessage, setShowMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productRates, setProductRates] = useState({}); // { productId: price }
  const [isLoading, setIsLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);

  // Fetch order data and products on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      // Clear previous order data to prevent showing wrong order
      setOrderData(null);
      setPickupItems([]);
      setProductRates({});
      
      try {
        // Fetch order data from API - get fresh data for this specific order
        const orderResult = await ordersService.getOrderById(orderId);
        if (orderResult.success && orderResult.data) {
          const apiOrderData = orderResult.data;
          
          // Verify we got the correct order
          const fetchedOrderId = apiOrderData.order_id || apiOrderData.id;
          if (fetchedOrderId && fetchedOrderId.toString() !== orderId.toString()) {
            console.error('Order ID mismatch! Expected:', orderId, 'Got:', fetchedOrderId);
            showNotification('error', 'Order data mismatch. Please try again.');
            return;
          }
          
          // Transform API response to app format for compatibility
          const transformedOrder = transformOrder(apiOrderData);
          if (transformedOrder) {
            setOrderData(transformedOrder);
            setCurrentPickup(transformedOrder);
          }
        }

        // Fetch products to get base prices
        const productsResult = await pickerService.getProducts();
        if (productsResult.success && productsResult.data && Array.isArray(productsResult.data)) {
          const rates = {};
          productsResult.data.forEach(product => {
            if (product.product_id) {
              const productId = product.product_id;
              const price = parseFloat(product.price || 0);
              rates[productId] = price;
            }
          });
          setProductRates(rates);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification('error', 'Failed to load order data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [orderId, setCurrentPickup, setPickupItems]);

  // Initialize pickupItems from order data
  useEffect(() => {
    if (!orderData || Object.keys(productRates).length === 0) return;

    // Check if order is already PAID - if so, prevent editing
    if (orderData.status === 'PAID' || orderData.status === 'COMPLETED') {
      setShowMessage({ type: 'error', text: 'This order has been completed and cannot be edited' });
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      return;
    }

    // Initialize pickupItems with existing order items
    // Use raw API data: orderData.items or orderData.order_products (from transformOrder)
    // These come directly from API response's "products" array
    const orderProducts = orderData.items || orderData.order_products || [];
    
    // Only initialize if we have products and pickupItems is empty (to prevent overwriting user edits)
    if (orderProducts.length > 0 && pickupItems.length === 0) {
      // Fetch product details to get product names
      const fetchProductDetails = async () => {
        try {
          const productsResult = await pickerService.getProducts();
          const productsMap = {};
          if (productsResult.success && productsResult.data) {
            productsResult.data.forEach(product => {
              if (product.product_id) {
                productsMap[product.product_id] = product;
              }
            });
          }

          const existingItems = orderProducts.map((item, index) => {
            // Handle API format: { id, product_id, quantity, estimate_price, image, customer_rate }
            const orderProductId = item.id || item.order_product_id;
            const estimatedQty = parseFloat(item.quantity) || 0;
            const estimatedPrice = parseFloat(item.estimate_price) || 0;
            const productId = item.product_id;
            
            // Get product details from products map
            const productDetails = productsMap[productId];
            const category = productDetails?.product_name || item.product?.product_name || item.product_name || 'Product';
            const description = productDetails?.description || item.product?.description || item.description || '';
            
            // Base price from products API (for display)
            const basePrice = productRates[productId] || (plasticRates[category] || 0);
            
            // Customer/selling rate from API - this comes directly from API response
            // API returns customer_rate as string (e.g., "8.25") or number
            let apiCustomerRate = null;
            if (item.customer_rate != null && item.customer_rate !== '' && item.customer_rate !== 'null') {
              const parsed = parseFloat(item.customer_rate);
              if (!isNaN(parsed) && parsed > 0) {
                apiCustomerRate = parsed;
              }
            }
            
            // Use API customer_rate if available, otherwise fallback to base price
            const customerRate = apiCustomerRate != null && !isNaN(apiCustomerRate) 
              ? apiCustomerRate 
              : basePrice;
            
            return {
              id: `existing-${orderProductId || productId || index}`,
              orderProductId: orderProductId,
              productId: productId,
              category: category,
              userQuantity: estimatedQty,
              actualQuantity: item.actual_quantity || null,
              weight: item.actual_quantity ? parseFloat(item.actual_quantity) : null,
              unit: productDetails?.unit || item.product?.unit || item.unit || 'kg',
              description: description,
              photo: item.image || productDetails?.photo || item.product?.image || item.product?.photo || null,
              rate: basePrice, // Base price for display
              customerRate: customerRate, // Selling rate from API or base price
              rateConfirmed: apiCustomerRate != null && !isNaN(apiCustomerRate), // Track if customer_rate exists in API
              hasCustomerRateFromAPI: apiCustomerRate != null && !isNaN(apiCustomerRate), // Explicit flag for API customer_rate
              negotiatedRateConfirmed: false, // Track if picker confirmed a negotiated rate via button
              isFromOrder: true
            };
          });
          setPickupItems(existingItems);
        } catch (error) {
          console.error('Error fetching product details:', error);
          // Fallback: create items without product details
          const existingItems = orderProducts.map((item, index) => {
            const orderProductId = item.id || item.order_product_id;
            const estimatedQty = parseFloat(item.quantity) || 0;
            const estimatedPrice = parseFloat(item.estimate_price) || 0;
            const productId = item.product_id;
            const category = item.product?.product_name || item.product_name || 'Product';
            
            // Base price from products API
            const basePrice = productRates[productId] || (estimatedQty > 0 ? (estimatedPrice / estimatedQty) : 0);
            
            // Customer/selling rate from API - this comes directly from API response
            // API returns customer_rate as string (e.g., "8.25") or number
            let apiCustomerRate = null;
            if (item.customer_rate != null && item.customer_rate !== '' && item.customer_rate !== 'null') {
              const parsed = parseFloat(item.customer_rate);
              if (!isNaN(parsed) && parsed > 0) {
                apiCustomerRate = parsed;
              }
            }
            const customerRate = apiCustomerRate != null && !isNaN(apiCustomerRate) 
              ? apiCustomerRate 
              : basePrice;
            
            return {
              id: `existing-${orderProductId || productId || index}`,
              orderProductId: orderProductId,
              productId: productId,
              category: category,
              userQuantity: estimatedQty,
              actualQuantity: item.actual_quantity || null,
              weight: item.actual_quantity ? parseFloat(item.actual_quantity) : null,
              unit: 'kg',
              description: item.description || '',
              photo: item.image || null,
              rate: basePrice, // Base price for display
              customerRate: customerRate, // Selling rate from API or base price
              rateConfirmed: apiCustomerRate != null && !isNaN(apiCustomerRate), // Track if customer_rate exists in API
              hasCustomerRateFromAPI: apiCustomerRate != null && !isNaN(apiCustomerRate), // Explicit flag for API customer_rate
              negotiatedRateConfirmed: false, // Track if picker confirmed a negotiated rate via button
              isFromOrder: true
            };
          });
          setPickupItems(existingItems);
        }
      };

      fetchProductDetails();
    }
  }, [orderData, productRates, pickupItems.length, setPickupItems, plasticRates, navigate]);

  const showNotification = (type, text) => {
    setShowMessage({ type, text });
    setTimeout(() => setShowMessage({ type: '', text: '' }), 3000);
  };

  const handleAddItem = () => {
    const newItemId = `item-${Date.now()}`;
    navigate(`/pickup/${orderId}/log-item/${newItemId}`);
  };

  const handleContinueToSummary = () => {
    // Check if all existing items have quantities
    const existingItems = pickupItems.filter(item => item.isFromOrder);
    
    if (existingItems.length > 0) {
      const allHaveQuantity = existingItems.every(item => item.weight != null && item.weight >= 0);
      
      if (!allHaveQuantity) {
        showNotification('error', 'Please add actual quantities for all items');
        return;
      }

      // Check if all items are confirmed
      const allConfirmed = existingItems.every(item => item.confirmed === true);
      
      if (!allConfirmed) {
        showNotification('error', 'Please confirm all items using the green tick button');
        return;
      }
    }

    // Check if we have at least one item with actual quantity (for additional items)
    const itemsWithQuantity = pickupItems.filter(item => item.weight != null && item.weight >= 0);
    if (itemsWithQuantity.length > 0) {
      navigate(`/pickup/${orderId}/summary`);
    } else {
      showNotification('error', 'Please add at least one item with quantity');
    }
  };

  const handleQuantityChange = (itemId, quantity) => {
    // Allow empty string, 0, or any positive number
    if (quantity === '' || quantity === null || quantity === undefined) {
      updatePickupItem(itemId, {
        actualQuantity: null,
        weight: null
      });
      return;
    }
    const quantityValue = parseFloat(quantity);
    if (!isNaN(quantityValue) && quantityValue >= 0) {
    updatePickupItem(itemId, {
      actualQuantity: quantityValue,
      weight: quantityValue
    });
    }
  };

  const handleQuantityIncrement = (itemId, currentValue) => {
    const newValue = (parseFloat(currentValue) || 0) + 0.5;
    handleQuantityChange(itemId, newValue);
  };

  const handleQuantityDecrement = (itemId, currentValue) => {
    const newValue = Math.max(0, (parseFloat(currentValue) || 0) - 0.5);
    handleQuantityChange(itemId, newValue);
  };

  const handleRateChange = (itemId, rate) => {
    if (rate === '' || rate === null || rate === undefined) {
      updatePickupItem(itemId, { 
        negotiatedRate: null,
        negotiatedRateConfirmed: false // Allow re-editing when user clears the field
      });
      return;
    }
    const rateValue = parseFloat(rate);
    if (!isNaN(rateValue) && rateValue >= 0) {
      updatePickupItem(itemId, { 
        negotiatedRate: rateValue,
        negotiatedRateConfirmed: false // Reset confirmation status when user edits
      });
    }
  };

  const handleRateIncrement = (itemId, currentRate) => {
    const newValue = (parseFloat(currentRate) || 0) + 0.5;
    handleRateChange(itemId, newValue);
  };

  const handleRateDecrement = (itemId, currentRate) => {
    const newValue = Math.max(0, (parseFloat(currentRate) || 0) - 0.5);
    handleRateChange(itemId, newValue);
  };

  const handleConfirmRate = async (item) => {
    const negotiatedRate = item.negotiatedRate != null ? parseFloat(item.negotiatedRate) : null;
    
    if (negotiatedRate == null || isNaN(negotiatedRate) || negotiatedRate < 0) {
      showNotification('error', 'Please enter a valid negotiated rate');
      return;
    }

    if (!item.orderProductId) {
      showNotification('error', 'Unable to confirm rate: Missing order product ID');
      return;
    }

    try {
      // Get current actual quantity (if any) or use 0
      const currentQuantity = parseFloat(item.actualQuantity) || parseFloat(item.weight) || 0;
      
      // Calculate actual price with new rate (if quantity exists)
      const actualPrice = currentQuantity > 0 ? Math.round(currentQuantity * negotiatedRate) : 0;

      setIsSubmitting(true);
      showNotification('success', 'Updating negotiated rate...');

      // Update API with customer_rate
      const result = await pickerService.updateOrderProduct(
        item.orderProductId,
        currentQuantity, // Use current quantity or 0
        actualPrice, // Calculated price or 0
        negotiatedRate // The negotiated customer_rate
      );

      if (result.success) {
        // Update local state with confirmed rate
        updatePickupItem(item.id, { 
          customerRate: negotiatedRate,
          rateConfirmed: true, // Mark that picker has confirmed a negotiated rate
          negotiatedRateConfirmed: true, // Track that picker confirmed via button
          negotiatedRate: negotiatedRate // Keep the confirmed rate visible in input field
        });
        showNotification('success', 'Negotiated rate confirmed successfully!');
      } else {
        showNotification('error', result.message || 'Failed to confirm negotiated rate');
      }
    } catch (error) {
      console.error('Error confirming rate:', error);
      showNotification('error', error.message || 'Failed to confirm negotiated rate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmItem = async (item) => {
    // Get the most recent actual quantity value
    const actualQuantity = parseFloat(item.actualQuantity) || parseFloat(item.weight) || 0;
    
    if (actualQuantity == null || actualQuantity < 0) {
      showNotification('error', 'Please enter a valid quantity for this item');
      return;
    }

    if (!item.orderProductId) {
      showNotification('error', 'Unable to confirm: Missing order product ID');
      return;
    }

    try {
      // Get base price from productRates (from products API), fallback to item.rate, then plasticRates
      const basePrice = item.productId && productRates[item.productId] 
        ? productRates[item.productId] 
        : (item.rate || (item.category && plasticRates[item.category]) || 0);
      
      // Use customer_rate (selling rate) if confirmed, otherwise use base price
      const sellingRate = item.customerRate != null && !isNaN(item.customerRate) && item.rateConfirmed
        ? parseFloat(item.customerRate)
        : basePrice;
      
      // Calculate actual price as selling rate * actual quantity, rounded to integer
      const actualPrice = Math.round(actualQuantity * sellingRate);

      setIsSubmitting(true);
      showNotification('success', 'Confirming item...');

      // Send customer_rate if it was confirmed, otherwise don't send it
      const customerRateToSend = item.rateConfirmed ? sellingRate : null;

      const result = await pickerService.updateOrderProduct(
        item.orderProductId,
        actualQuantity,
        actualPrice,
        customerRateToSend
      );

      if (result.success) {
        // Mark item as confirmed locally (visual feedback)
        updatePickupItem(item.id, { confirmed: true, actualPrice: actualPrice });
        showNotification('success', 'Item confirmed successfully!');
      } else {
        showNotification('error', result.message || 'Failed to confirm item');
      }
    } catch (error) {
      console.error('Error confirming item:', error);
      showNotification('error', error.message || 'Failed to confirm item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditItem = (itemId) => {
    // Unconfirm the item to allow editing
    updatePickupItem(itemId, { confirmed: false });
    showNotification('info', 'Item unlocked for editing');
  };

  if (isLoading) {
    return (
      <div className="pickup-process-screen">
        <div className="pickup-process-container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div className="pd-spinner" style={{ width: '40px', height: '40px' }}></div>
            <p style={{ color: theme === 'dark' ? '#94a3b8' : '#6b7280', fontSize: '14px' }}>Loading order data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData && !currentPickup) {
    return (
      <div className="pickup-process-screen">
        <div className="pickup-process-container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <p style={{ color: theme === 'dark' ? '#94a3b8' : '#6b7280', fontSize: '14px' }}>Order not found</p>
            <button 
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '12px 24px',
                background: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const displayPickup = orderData || currentPickup;

  const existingItems = pickupItems.filter(item => item.isFromOrder);
  const additionalItems = pickupItems.filter(item => !item.isFromOrder);
  const completedExistingItems = existingItems.filter(item => item.weight != null && item.weight >= 0);
  const totalItemsWithQuantity = pickupItems.filter(item => item.weight != null && item.weight >= 0).length;
  const totalWeight = pickupItems.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);

  // Calculate progress
  const progress = existingItems.length > 0 
    ? (completedExistingItems.length / existingItems.length) * 100 
    : (totalItemsWithQuantity > 0 ? 100 : 0);

  return (
    <div className="pickup-process-screen">
      <div className="pickup-process-container">
        {/* Header */}
        <div className="process-header">
          <button className="header-back-btn" onClick={() => navigate(`/pickup/${orderId}`)}>
            <ArrowLeftIcon size={24} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
          </button>
          <div className="header-title-wrapper">
            <h1 className="header-title">PICKUP PROCESS</h1>
            <div className="header-order-number">
              Order #{displayPickup?.order_id || orderId}
            </div>
          </div>
          <div style={{ width: 40 }}></div>
        </div>

        {/* Progress Indicator */}
        <div className="progress-section">
          <div className="progress-header">
            <div className="progress-label">Progress</div>
            <div className="progress-percentage">{Math.round(progress)}%</div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-stats">
            <div className="progress-stat">
              <ScaleIcon size={16} color={theme === 'dark' ? '#94a3b8' : '#6b7280'} />
              <span>{totalWeight.toFixed(1)} kg</span>
            </div>
            <div className="progress-stat">
              <PackageIcon size={16} color={theme === 'dark' ? '#94a3b8' : '#6b7280'} />
              <span>{totalItemsWithQuantity} items</span>
            </div>
          </div>
        </div>

        {/* Notification Message */}
        {showMessage.text && (
          <div className={`notification-message ${showMessage.type}`}>
            {showMessage.text}
          </div>
        )}

        {/* Order Items Section */}
        {existingItems.length > 0 && (
          <div className="items-section">
            <div className="section-header">
              <div className="section-header-content">
                <h3 className="section-title">Order Items</h3>
                <p className="section-subtitle">
                  {completedExistingItems.length} of {existingItems.length} completed
                </p>
              </div>
            </div>
            <div className="items-list">
              {existingItems.map((item) => {
                const hasQuantity = item.weight != null && item.weight >= 0;
                const isConfirmed = item.confirmed === true;
                const actualQty = item.actualQuantity != null ? parseFloat(item.actualQuantity) : (item.weight != null ? parseFloat(item.weight) : null);
                const currentValue = actualQty != null && !isNaN(actualQty) ? actualQty : '';
                
                // Get base price from productRates (from products API), fallback to item.rate, then plasticRates
                const basePrice = item.productId && productRates[item.productId] 
                  ? productRates[item.productId] 
                  : (item.rate || (item.category && plasticRates[item.category]) || 0);
                
                // Selling rate (customer_rate from API, or base price if not available)
                // customerRate is set from API's customer_rate field when item is initialized
                const sellingRate = item.customerRate != null && !isNaN(item.customerRate)
                  ? parseFloat(item.customerRate)
                  : basePrice;
                
                // Negotiated rate (user input or confirmed rate) - starts empty, shows confirmed value after confirmation
                const negotiatedRateValue = item.negotiatedRate != null && !isNaN(item.negotiatedRate)
                  ? parseFloat(item.negotiatedRate)
                  : (item.negotiatedRateConfirmed && item.customerRate != null && !isNaN(item.customerRate)
                      ? parseFloat(item.customerRate)
                      : '');
                
                // Actual price calculation: use selling rate if confirmed, otherwise use negotiated rate if entered, else base price
                const rateForCalculation = item.rateConfirmed 
                  ? sellingRate 
                  : (item.negotiatedRate != null && !isNaN(item.negotiatedRate) 
                      ? parseFloat(item.negotiatedRate) 
                      : basePrice);
                
                const actualPrice = currentValue !== '' && currentValue >= 0 && rateForCalculation > 0
                  ? Math.round(currentValue * rateForCalculation)
                  : 0;
                
                return (
                  <div key={item.id} className={`item-card existing-item ${hasQuantity ? 'completed' : ''} ${isConfirmed ? 'confirmed' : ''}`}>
                    <div className="item-content">
                      <div className="item-header">
                        <div className="item-info">
                          <div className="item-category">{item.category}</div>
                          {item.productId && (
                            <div className="product-id">Product ID: #{item.productId}</div>
                          )}
                          {basePrice > 0 && (
                            <div className="product-price">Base Price: ₹{basePrice.toFixed(2)}/kg</div>
                          )}
                          {item.userQuantity != null && (
                            <div className="user-quantity">
                              Expected: {item.userQuantity} {item.unit || 'qty'}
                            </div>
                          )}
                          {/* Show customer quoted rate (from API customer_rate in order details) */}
                          {item.hasCustomerRateFromAPI && item.customerRate != null && !isNaN(item.customerRate) && item.customerRate > 0 && (
                            <div className="user-quantity" style={{ color: '#10b981', fontWeight: '500' }}>
                              Customer Quoted Rate: ₹{item.customerRate.toFixed(2)}/kg
                            </div>
                          )}
                        </div>
                        {item.photo && (
                          <div className="item-photo-preview">
                            <img src={item.photo} alt={item.category} />
                          </div>
                        )}
                        {isConfirmed && !item.photo && (
                          <div className="completed-badge">
                            <CheckIcon size={14} color="#ffffff" />
                          </div>
                        )}
                      </div>

                      {item.description && (
                        <div className="item-description">{item.description}</div>
                      )}

                      {/* Negotiated Rate Section - Before Actual Weight */}
                      <div className="quantity-section">
                        <label className="quantity-label">NEGOTIATED RATE (₹/KG)</label>
                        <div className="quantity-controls">
                          <button
                            className="quantity-btn decrement"
                            onClick={() => handleRateDecrement(item.id, negotiatedRateValue || 0)}
                            type="button"
                            disabled={isConfirmed || isSubmitting}
                          >
                            <MinusIcon size={16} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
                          </button>
                          <input
                            type="number"
                            className={`quantity-input ${item.negotiatedRateConfirmed ? 'filled' : ''}`}
                            step="0.5"
                            min="0"
                            placeholder="0.0"
                            value={negotiatedRateValue === '' ? '' : negotiatedRateValue}
                            onChange={(e) => handleRateChange(item.id, e.target.value)}
                            disabled={isConfirmed || isSubmitting}
                          />
                          <button
                            className="quantity-btn increment"
                            onClick={() => handleRateIncrement(item.id, negotiatedRateValue || 0)}
                            type="button"
                            disabled={isConfirmed || isSubmitting}
                          >
                            <PlusIcon size={16} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
                          </button>
                          <button
                            className="confirm-item-btn"
                            onClick={() => handleConfirmRate(item)}
                            disabled={!negotiatedRateValue || isNaN(negotiatedRateValue) || negotiatedRateValue <= 0 || isConfirmed || isSubmitting}
                            type="button"
                            style={{ backgroundColor: '#10b981' }}
                          >
                            <CheckIcon size={18} color="#ffffff" />
                          </button>
                        </div>
                        {/* Show final negotiated rate after confirmation */}
                        {item.negotiatedRateConfirmed && item.customerRate > 0 && (
                          <div className="item-price" style={{ color: '#10b981', fontWeight: '600' }}>
                            Final Negotiated Rate: ₹{item.customerRate.toFixed(2)}/kg
                          </div>
                        )}
                      </div>

                      <div className="quantity-section">
                        <label className="quantity-label">ACTUAL WEIGHT (KG)</label>
                        <div className="quantity-controls">
                          <button
                            className="quantity-btn decrement"
                            onClick={() => handleQuantityDecrement(item.id, currentValue)}
                            type="button"
                            disabled={isConfirmed || isSubmitting}
                          >
                            <MinusIcon size={16} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
                          </button>
                          <input
                            type="number"
                            className={`quantity-input ${hasQuantity ? 'filled' : ''}`}
                            step="0.5"
                            min="0"
                            placeholder="0.0"
                            value={currentValue === '' ? '' : currentValue}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            disabled={isConfirmed || isSubmitting}
                            required
                          />
                          <button
                            className="quantity-btn increment"
                            onClick={() => handleQuantityIncrement(item.id, currentValue)}
                            type="button"
                            disabled={isConfirmed || isSubmitting}
                          >
                            <PlusIcon size={16} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
                          </button>
                          <button
                            className="confirm-item-btn"
                            onClick={() => handleConfirmItem(item)}
                            disabled={!hasQuantity || isConfirmed || isSubmitting}
                            type="button"
                          >
                            <CheckIcon size={18} color="#ffffff" />
                          </button>
                        </div>
                        {currentValue !== '' && currentValue >= 0 && rateForCalculation > 0 && (
                          <div className="item-price">
                            Actual Price: ₹{actualPrice}
                          </div>
                        )}
                        {isConfirmed && (
                          <div className="confirmed-indicator">
                            <CheckIcon size={16} color="#10b981" />
                            <span>Confirmed</span>
                            <button
                              className="edit-item-btn-small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditItem(item.id);
                              }}
                              type="button"
                              title="Edit quantity"
                            >
                              <EditIcon size={14} color="#3b82f6" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Additional Items Section */}
        <div className="items-section">
          <div className="section-header">
            <div className="section-header-content">
              <h3 className="section-title">
                {existingItems.length > 0 ? 'Additional Items' : 'Items'}
              </h3>
              <p className="section-subtitle">
                {existingItems.length > 0 
                  ? 'Add more products if needed' 
                  : 'Start logging collected items'}
              </p>
            </div>
            <button className="add-item-btn" onClick={handleAddItem}>
              <PlusIcon size={20} color="#ffffff" />
              Add Item
            </button>
          </div>

          {additionalItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon-wrapper">
                <PackageIcon size={48} color={theme === 'dark' ? '#475569' : '#cbd5e1'} />
              </div>
              <p className="empty-text">
                {existingItems.length > 0 
                  ? 'No additional items added yet' 
                  : 'No items logged yet'}
              </p>
              <p className="empty-subtext">
                {existingItems.length > 0
                  ? 'Tap "Add Item" to log more products'
                  : 'Tap "Add Item" to start logging'}
              </p>
            </div>
          ) : (
            <div className="items-list">
              {additionalItems.map((item) => (
                <div key={item.id} className="item-card">
                  <div className="item-content">
                    <div className="item-header">
                      <div className="item-info">
                        <div className="item-category">{item.category}</div>
                        <div className="item-weight">{item.weight || 0} kg</div>
                      </div>
                    </div>
                    {item.description && (
                      <div className="item-description">{item.description}</div>
                    )}
                    {item.photo && (
                      <div className="item-photo-preview">
                        <img src={item.photo} alt={item.category} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Continue Button - Single button for all cases */}
        <div className="confirm-section">
          <button 
            className={`btn-primary confirm-items-btn ${completedExistingItems.length === existingItems.length && existingItems.length > 0 ? 'ready' : ''}`}
            onClick={handleContinueToSummary}
            disabled={existingItems.length > 0 && completedExistingItems.length !== existingItems.length}
          >
            {existingItems.length > 0 
              ? (completedExistingItems.length === existingItems.length 
                  ? (
                      <span className="button-text-multiline">
                        ✓ All Items Confirmed - View<br />Summary
                      </span>
                    )
                  : `Confirm ${completedExistingItems.length}/${existingItems.length} Items`)
              : (totalItemsWithQuantity > 0 
                  ? 'View Summary'
                  : 'Add Items to Continue')}
            <ArrowRightIcon size={20} color="#ffffff" className="btn-icon-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickupProcess;
